<!--
  날짜 입력 공용 필드.

  두 가지 문제를 한꺼번에 푼다.

  1) 연도 선택 — Vuetify 의 v-date-picker 는 헤더에서 월까지만 열리고 연도 목록이 없다.
     1985 년생 생년월일을 넣으려면 이전 달 버튼을 수백 번 눌러야 했다.
     여기서는 브라우저 기본 날짜 피커(`input[type=date]`)를 띄운다 — 연도를 바로 고를 수 있고
     키보드로 직접 타이핑도 된다.

  2) 포맷 통일 — 화면마다 "31-12-2030", "1 DECEMBER 1979", "15/03/1985" 가 섞여 있었다.
     표시는 **헤더에서 사용자가 고른 날짜 포맷**(sessionStore.user.user.dateFormat)을 따르고,
     저장은 **항상 ISO(YYYY-MM-DD)** 로 한다.

  저장을 ISO 로 고정한 이유가 중요하다. CRS 변환기(toCrsEpochMs)는 숫자 구분자 포맷을
  DD/MM 으로 **가정**해서 읽는다. 저장값이 사용자 포맷을 따라다니면, 사용자가 MM/DD/YYYY 로
  바꾼 순간 CRS 에 월과 일이 뒤바뀐 날짜가 제출된다. 저장이 ISO 면 그 분기를 타지 않는다.

  입력 파싱은 관대하다 — ISO, DD-MM-YYYY, DD/MM/YYYY, "1 December 1979" 를 모두 받는다.
  (기존 데이터가 이 포맷들로 남아 있어서 표시할 때도 그대로 읽어야 한다.)
-->
<template>
  <v-text-field
    :model-value="display"
    @update:model-value="onTextInput"
    @focus="onFocus"
    @blur="onBlur"
    :label="label"
    :density="density"
    :variant="variant"
    :readonly="readonly"
    :disabled="disabled"
    :hide-details="hideDetails"
    :error="error"
    :error-messages="errorMessages"
    :placeholder="placeholder || userFormat"
    :hint="hint"
    :persistent-hint="!!hint"
    maxlength="20"
  >
    <template v-if="required" #label>
      {{ label }} <span class="text-error">*</span>
    </template>
    <template #append-inner>
      <v-icon
        size="small"
        :style="{ cursor: readonly || disabled ? 'default' : 'pointer', opacity: .6 }"
        @click.stop="openPicker"
      >mdi-calendar</v-icon>
      <input
        ref="pickerRef"
        type="date"
        :value="isoValue"
        :min="min"
        :max="max"
        @input="onPickerChange"
        tabindex="-1"
        aria-hidden="true"
        style="position:absolute;right:0;bottom:0;width:0;height:0;opacity:0;pointer-events:none;"
      />
    </template>
  </v-text-field>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useSessionStore } from '@/shared/stores/sessionStore';

const props = withDefaults(defineProps<{
    modelValue?: string | null;
    label?: string;
    required?: boolean;
    placeholder?: string;
    hint?: string;
    density?: 'default' | 'comfortable' | 'compact';
    variant?: 'outlined' | 'filled' | 'underlined' | 'plain' | 'solo';
    readonly?: boolean;
    disabled?: boolean;
    hideDetails?: boolean | 'auto';
    error?: boolean;
    errorMessages?: string | string[];
    /** input[type=date] 로 넘길 범위. YYYY-MM-DD */
    min?: string;
    max?: string;
}>(), {
    modelValue: '',
    label: '',
    required: false,
    placeholder: '',
    hint: '',
    density: 'compact',
    variant: 'outlined',
    readonly: false,
    disabled: false,
    hideDetails: false,
    error: false,
});

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();
const pickerRef = ref<HTMLInputElement | null>(null);
/** 타이핑 중인 원문. 확정 전에는 이걸 그대로 보여 줘야 커서가 튀지 않는다. */
const typing = ref<string | null>(null);

const MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

/** 헤더에서 고른 사용자 날짜 포맷. 못 읽으면 말레이시아 표기를 쓴다. */
const userFormat = computed(() => {
    try {
        return String(useSessionStore().user?.user?.dateFormat || 'DD/MM/YYYY').toUpperCase();
    } catch {
        return 'DD/MM/YYYY';
    }
});

/**
 * 포맷 토큰. 긴 것부터 적어야 MMMM 이 MM 두 개로 쪼개지지 않는다.
 *
 * 예전에는 `/YYYY|MM|DD/g` 로 훑어서 'D MMMM YYYY'(계정 설정의 "d MMMM yyyy")가
 * ['MM','MM','YYYY'] 로 잡혔다 — 일(日) 토큰이 아예 없으니 타이핑한 날짜가 전부 버려지고,
 * 화면에도 일 자리에 글자 D 가 그대로 남았다.
 */
const TOKEN_RE = /YYYY|YY|MMMM|MMM|MM|M|DD|D/g;

/** 포맷에서 연·월·일이 나오는 순서. 'D MMMM YYYY' → ['D','M','Y'] */
function formatKinds(fmt: string): string[] {
    return (fmt.match(TOKEN_RE) || []).map((t) => t[0]);
}

/** 어떤 포맷이든 → YYYY-MM-DD. 못 읽으면 ''. */
function parseAny(raw: string): string {
    const v = String(raw || '').trim();
    if (!v) return '';

    const iso = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) return fmtIso(+iso[1], +iso[2], +iso[3]);

    // "1 December 1979" / "1 Dec 1979"
    const long = v.match(/^(\d{1,2})\s+([A-Za-z]+)\.?\s+(\d{4})$/);
    if (long) {
        const mi = MONTHS.findIndex((m) => m.startsWith(long[2].toUpperCase()));
        if (mi >= 0) return fmtIso(+long[3], mi + 1, +long[1]);
        return '';
    }

    // 숫자 3토막 — 사용자 포맷의 토큰 순서대로 읽는다.
    const parts = v.match(/^(\d{1,4})[-/.](\d{1,2})[-/.](\d{1,4})$/);
    if (!parts) return '';
    // 4자리가 앞에 오면 무조건 연도다(사용자 포맷과 무관하게 ISO 입력을 허용).
    if (parts[1].length === 4) return fmtIso(+parts[1], +parts[2], +parts[3]);
    const order = formatKinds(userFormat.value);
    if (order.length !== 3) return '';
    const val: Record<string, number> = {};
    order.forEach((kind, i) => { val[kind] = +parts[i + 1]; });
    const y = val.Y, m = val.M, d = val.D;
    if (!y || !m || !d) return '';
    return fmtIso(y, m, d);
}

function fmtIso(y: number, m: number, d: number): string {
    if (!y || m < 1 || m > 12 || d < 1 || d > 31) return '';
    const p2 = (n: number) => String(n).padStart(2, '0');
    return `${y}-${p2(m)}-${p2(d)}`;
}

/** 저장값 → YYYY-MM-DD. 피커와 내부 계산에 쓴다. */
const isoValue = computed(() => parseAny(String(props.modelValue || '')));

/** 화면 표시 — 사용자 포맷. */
const display = computed(() => {
    if (typing.value !== null) return typing.value;
    const iso = isoValue.value;
    // 못 읽는 값이면 원문을 그대로 보여 준다(값을 삼키지 않는다).
    if (!iso) return String(props.modelValue || '');
    const [y, m, d] = iso.split('-');
    // 토큰을 한 번에 치환한다 — 순차 replace 는 앞 단계가 만든 글자를 뒷 단계가 다시 집는다.
    return userFormat.value.replace(TOKEN_RE, (tok) => {
        switch (tok) {
            case 'YYYY': return y;
            case 'YY': return y.slice(2);
            case 'MMMM': return MONTHS[+m - 1];
            case 'MMM': return MONTHS[+m - 1].slice(0, 3);
            case 'MM': return m;
            case 'M': return String(+m);
            case 'DD': return d;
            case 'D': return String(+d);
            default: return tok;
        }
    });
});

function onTextInput(val: string) {
    typing.value = val;
    if (!val) { emit('update:modelValue', ''); return; }
    const iso = parseAny(val);
    if (iso) emit('update:modelValue', iso);
}

/**
 * 포커스가 들어오면 달력을 바로 띄운다.
 *
 * 예전에는 오른쪽 달력 아이콘을 눌러야만 열려서, 날짜 하나 고르는 데 클릭이 두 번 필요했다.
 * 필드를 클릭하면 그 클릭이 사용자 제스처로 인정돼 `showPicker()` 가 허용된다.
 * 탭 이동 등 제스처가 없는 포커스에서는 브라우저가 막는데, 그건 예외로 삼켜서
 * **타이핑 입력을 방해하지 않는다**(직접 입력도 계속 가능).
 */
function onFocus() { openPicker(); }

/** 포커스를 잃으면 타이핑 원문을 버리고 저장값 기준 표시로 되돌린다. */
function onBlur() { typing.value = null; }

function openPicker() {
    if (props.readonly || props.disabled) return;
    const el = pickerRef.value;
    if (!el) return;
    // showPicker 를 막는 브라우저에서는 클릭으로 대체한다.
    if (typeof el.showPicker === 'function') {
        try { el.showPicker(); return; } catch { /* 아래로 */ }
    }
    el.style.pointerEvents = 'auto';
    el.click();
    el.style.pointerEvents = 'none';
}

function onPickerChange(e: Event) {
    const v = (e.target as HTMLInputElement).value;
    typing.value = null;
    emit('update:modelValue', v);
}
</script>
